import React, { useState, useCallback } from 'react';

type Remote = {
  name: string;
  url: string;
  type: 'fetch' | 'push' | 'both';
};

type RemoteManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  remotes: string[];
  onAddRemote?: (name: string, url: string) => Promise<boolean>;
  onRemoveRemote?: (name: string) => Promise<boolean>;
};

export const RemoteManagementModal = React.memo(function RemoteManagementModal({
  isOpen,
  onClose,
  remotes,
  onAddRemote,
  onRemoveRemote,
}: RemoteManagementModalProps) {
  const [newRemoteName, setNewRemoteName] = useState('');
  const [newRemoteUrl, setNewRemoteUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Simulated remote URLs for demo
  const remoteDetails: Remote[] = remotes.map(name => ({
    name,
    url: name === 'origin'
      ? 'https://github.com/user/project.git'
      : `https://github.com/${name}/project.git`,
    type: 'both',
  }));

  const handleAddRemote = useCallback(async () => {
    if (!newRemoteName.trim() || !newRemoteUrl.trim()) {
      setError('Please enter both name and URL');
      return;
    }

    if (remotes.includes(newRemoteName.trim())) {
      setError('Remote with this name already exists');
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      if (onAddRemote) {
        const success = await onAddRemote(newRemoteName.trim(), newRemoteUrl.trim());
        if (success) {
          setNewRemoteName('');
          setNewRemoteUrl('');
          setShowAddForm(false);
        } else {
          setError('Failed to add remote');
        }
      } else {
        // Demo mode - just show success
        setNewRemoteName('');
        setNewRemoteUrl('');
        setShowAddForm(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add remote');
    } finally {
      setIsAdding(false);
    }
  }, [newRemoteName, newRemoteUrl, remotes, onAddRemote]);

  const handleRemoveRemote = useCallback(async (name: string) => {
    if (name === 'origin') {
      setError('Cannot remove the origin remote');
      return;
    }

    setIsRemoving(name);
    setError(null);

    try {
      if (onRemoveRemote) {
        const success = await onRemoveRemote(name);
        if (!success) {
          setError('Failed to remove remote');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove remote');
    } finally {
      setIsRemoving(null);
    }
  }, [onRemoveRemote]);

  const handleClose = useCallback(() => {
    setError(null);
    setShowAddForm(false);
    setNewRemoteName('');
    setNewRemoteUrl('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal remote-management-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '550px' }}
      >
        <div className="modal-header">
          <div className="modal-title">Git Remotes</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          {/* Remote list */}
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            {remoteDetails.length === 0 ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: '13px',
              }}>
                No remotes configured
              </div>
            ) : (
              remoteDetails.map((remote, index) => (
                <div
                  key={remote.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderBottom: index < remoteDetails.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    background: remote.name === 'origin' ? 'var(--accent)' : 'var(--bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: remote.name === 'origin' ? 'var(--bg-primary)' : 'var(--text)',
                  }}>
                    {remote.name[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '13px' }}>
                      {remote.name}
                      {remote.name === 'origin' && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '10px',
                          padding: '2px 6px',
                          background: 'var(--accent)',
                          color: 'var(--bg-primary)',
                          borderRadius: '10px',
                        }}>
                          default
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: 'var(--muted)',
                      fontFamily: 'monospace',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {remote.url}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => navigator.clipboard.writeText(remote.url)}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        color: 'var(--text)',
                        fontSize: '11px',
                        cursor: 'pointer',
                      }}
                      title="Copy URL"
                    >
                      Copy
                    </button>
                    {remote.name !== 'origin' && (
                      <button
                        onClick={() => handleRemoveRemote(remote.name)}
                        disabled={isRemoving === remote.name}
                        style={{
                          padding: '4px 8px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--error)',
                          borderRadius: '4px',
                          color: 'var(--error)',
                          fontSize: '11px',
                          cursor: isRemoving === remote.name ? 'not-allowed' : 'pointer',
                          opacity: isRemoving === remote.name ? 0.5 : 1,
                        }}
                      >
                        {isRemoving === remote.name ? '...' : 'Remove'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add remote form */}
          {showAddForm ? (
            <div style={{
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '4px',
              marginBottom: '12px',
            }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                  Remote name
                </label>
                <input
                  type="text"
                  value={newRemoteName}
                  onChange={(e) => setNewRemoteName(e.target.value)}
                  placeholder="e.g., upstream"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                  Remote URL
                </label>
                <input
                  type="text"
                  value={newRemoteUrl}
                  onChange={(e) => setNewRemoteUrl(e.target.value)}
                  placeholder="https://github.com/user/repo.git"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewRemoteName('');
                    setNewRemoteUrl('');
                    setError(null);
                  }}
                  className="pill-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRemote}
                  disabled={isAdding || !newRemoteName.trim() || !newRemoteUrl.trim()}
                  className="pill-btn"
                  style={{
                    opacity: isAdding || !newRemoteName.trim() || !newRemoteUrl.trim() ? 0.5 : 1,
                  }}
                >
                  {isAdding ? 'Adding...' : 'Add Remote'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                width: '100%',
                padding: '10px',
                background: 'var(--bg-secondary)',
                border: '1px dashed var(--border)',
                borderRadius: '4px',
                color: 'var(--muted)',
                fontSize: '13px',
                cursor: 'pointer',
                marginBottom: '12px',
              }}
            >
              + Add Remote
            </button>
          )}

          {error && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--error)',
              borderRadius: '4px',
              color: 'var(--error)',
              fontSize: '12px',
            }}>
              {error}
            </div>
          )}

          {/* Info */}
          <div style={{
            marginTop: '12px',
            fontSize: '11px',
            color: 'var(--muted)',
          }}>
            Remotes are used to track remote repositories for push and pull operations.
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default RemoteManagementModal;
